import { useLazyQuery } from "@apollo/client/react";
import TextField from "@mui/material/TextField";
import debounce from "lodash/debounce";
import { gql } from "graphql-tag";
import { GetDreamSearchParametersFromSearchDocument } from "./generated";
import Autocomplete from "@mui/material/Autocomplete";
import { useRouter } from "next/navigation";
import { getUrls } from "@portal/app/_common/lib/urls";
import { useEffect, useMemo, useRef, useState } from "react";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { getQueryParamsFromFilters } from "@portal/app/health/_components/utils/queryParamFiltersForDreamSearch";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import { faMagnifyingGlass } from "@fortawesome/pro-solid-svg-icons/faMagnifyingGlass";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { usePageContext } from "@portal/app/_components/context/PageContext";
import { useMixpanel } from "@portal/app/_components/hooks/useMixpanel";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import Link from "next/link";
import { VocationIndustryToName } from "@portal/app/_common/types/vocationIndustry";
import { faSpinner } from "@fortawesome/pro-solid-svg-icons/faSpinner";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { paletteOptions } from "../theme/options/palette";

void gql`
  query getDreamSearchParametersFromSearch($searchTerm: String!) {
    getDreamSearchParametersFromSearch(searchTerm: $searchTerm) {
      vocations {
        vocation {
          id
          name
          industry
          adjective
        }
        relatedVocations {
          id
          name
        }
      }
    }
  }
`;

const industryLinks: GQL.VocationIndustry[] = [
  "HEALTH",
  "BUSINESS_AND_OFFICE",
  "TRADE_AND_INDUSTRY",
  "TECHNICAL_AND_COMMUNICATIONS",
];

type GTMTrackingEvent = "nav_mobile_search" | "nav_desktop_search";
export const VocationSearchAutocomplete = ({
  zip,
  trackingEvent,
}: {
  zip: string | null | undefined;
  trackingEvent: GTMTrackingEvent;
}) => {
  const analytics = useMixpanel();
  const router = useRouter();
  const { isMobile } = usePageContext();
  const autocompleteRef = useRef<HTMLDivElement | null>(null);
  const urls = getUrls();
  const [displayedVocations, setDisplayedVocations] = useState<
    {
      vocation: {
        id: string;
        name: string;
        industry: GQL.VocationIndustry;
        adjective: string;
      };
      relatedVocations: {
        id: string;
        name: string;
      }[];
    }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [search, { loading: searchLoading }] = useLazyQuery(
    GetDreamSearchParametersFromSearchDocument,
    { notifyOnNetworkStatusChange: true }
  );
  const searchTermRef = useRef<string>("");
  const latestSearchRequestRef = useRef(0);

  const handleSearchTerm = useMemo(
    () =>
      debounce(async (searchTerm: string, requestId: number) => {
        if (!searchTerm.trim()) {
          if (requestId === latestSearchRequestRef.current) {
            setDisplayedVocations([]);
          }
          return;
        }

        // Keep prior suggestions on failure and contain superseded/unmounted requests.
        const result = await search({ variables: { searchTerm } }).catch(() => undefined);
        const nextVocations = result?.data?.getDreamSearchParametersFromSearch.vocations;
        if (requestId === latestSearchRequestRef.current && nextVocations) {
          setDisplayedVocations(nextVocations);
        }
        return searchTerm;
      }, 300),
    [search]
  );

  useEffect(
    () => () => {
      handleSearchTerm.cancel();
      latestSearchRequestRef.current++;
    },
    [handleSearchTerm]
  );

  const handleSearchTermTracking = useMemo(
    () =>
      debounce(async (searchTerm: string) => {
        searchTermRef.current = searchTerm;
        await analytics.track("Dream Search - Vocation Search", {
          searchTerm: searchTerm.toLowerCase(),
          trackingEvent,
        });
        return (
          searchTerm &&
          window.dataLayer.push({
            event: trackingEvent,
            searchTerm: searchTerm.toLowerCase(),
          })
        );
      }, 2000),
    [analytics, trackingEvent]
  );

  const handleSelectedOption = async (
    vocationId: string | undefined | null,
    relatedVocationId: string | undefined | null
  ) => {
    if (!vocationId) {
      return;
    }
    setLoading(true);
    await analytics.track("Dream Search - Vocation Search - Clicked", {
      searchTerm: searchTermRef.current,
      vocationId,
      relatedVocationId,
    });
    const vocation = displayedVocations.find((v) => v.vocation.id === vocationId);
    return router.push(
      urls.DreamSearch({
        industry: vocation?.vocation.industry,
        zip,
        filterQueryParams: getQueryParamsFromFilters({ program_vocation_in: [vocationId] }),
      })
    );
  };

  return (
    <Autocomplete
      id={`vocation-search-${trackingEvent}`}
      componentsProps={{
        popper: { ref: autocompleteRef },
      }}
      renderOption={(props, option) =>
        option.vocation.id !== "fallback" ? (
          <StyledListItem {...props} data-vocation-id={option.vocation.id} key={option.vocation.id}>
            <ListItemContent>
              <ListItemText primary={option.vocation.name} />
              {option.relatedVocations?.length > 0 && (
                <>
                  <Typography variant="caption" color={paletteOptions.dream.grayMediumDark}>
                    Related searches
                  </Typography>
                  <RecommendationContainer>
                    {option.relatedVocations?.map((rv) => (
                      <Chip
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          void handleSelectedOption(rv.id, option.vocation.id);
                        }}
                        key={option.vocation.id + "-" + rv.id}
                        label={rv.name}
                      />
                    ))}
                  </RecommendationContainer>
                </>
              )}
            </ListItemContent>
          </StyledListItem>
        ) : (
          <IndustrySearchContainer
            data-testid="industry-list-fallback"
            data-vocation-id="no-options"
            key="no-options"
          >
            <IndustrySearchTitle>Search by industry</IndustrySearchTitle>
            <IndustrySearchList>
              {industryLinks.map((industry) => (
                <IndustryLink
                  href={urls.DreamSearch({ industry, zip: zip ?? undefined })}
                  key={industry}
                >
                  {VocationIndustryToName[industry]}
                </IndustryLink>
              ))}

              <IndustryLink href={urls.DreamSearch({ zip: zip ?? undefined })}>All</IndustryLink>
            </IndustrySearchList>
          </IndustrySearchContainer>
        )
      }
      sx={{ width: "100%", maxWidth: 600 }}
      disableClearable
      autoHighlight
      clearOnBlur={false}
      slots={{
        paper: StyledPaper,
        popper: StyledPopper,
      }}
      slotProps={{
        listbox: {
          sx: {
            padding: "0rem",
          },
        },
      }}
      openOnFocus={true}
      options={[
        ...(displayedVocations.length > 0
          ? displayedVocations
          : [{ vocation: { id: "fallback", name: "Fallback" }, relatedVocations: [] }]),
      ]}
      disabled={loading}
      isOptionEqualToValue={(option, value) => option.vocation.id === value.vocation.id}
      filterOptions={(x) => x} // disables the default filtering https://mui.com/material-ui/react-autocomplete/#asynchronous-requests
      getOptionLabel={(option) => option.vocation.name}
      onInputChange={(event, value) => {
        // Exclude the "click" event, so we don't make a request when the user clicks an option
        if (event.type === "change") {
          const requestId = ++latestSearchRequestRef.current;
          return Promise.all([handleSearchTerm(value, requestId), handleSearchTermTracking(value)]);
        }
      }}
      onChange={(_event, clickedValue) => handleSelectedOption(clickedValue?.vocation.id, null)}
      renderInput={(params) => (
        <AutocompleteTextField
          {...params}
          placeholder="Search by program type"
          variant="standard"
          slotProps={{
            input: {
              ...params.InputProps,
              disableUnderline: true,
              startAdornment: (
                <AdornmentIconContainer>
                  <FontAwesomeIcon
                    icon={searchLoading ? faSpinner : faMagnifyingGlass}
                    spin={searchLoading}
                  />
                </AdornmentIconContainer>
              ),
            },
            htmlInput: {
              "data-hj-allow": true,
              ...params.inputProps,
            },
          }}
          InputProps={{
            endAdornment: !isMobile && (
              <Button
                onClick={() =>
                  handleSelectedOption(
                    autocompleteRef.current
                      ?.querySelector(".Mui-focused")
                      ?.getAttribute("data-vocation-id"),
                    null
                  )
                }
              >
                Search
              </Button>
            ),
          }}
          data-testid="vocation-search"
        />
      )}
    />
  );
};

const StyledPopper = styled(Popper)`
  border-radius: 0 0 1rem 1rem;
  box-shadow: none;
`;

const StyledPaper = styled(Paper)`
  border-top: none;
  border-radius: 0 0 1rem 1rem;
`;

const AutocompleteTextField = styled(TextField)`
  --border-radius: 1rem;
  --border-size: 2px;
  --input-height: 48px;
  width: 100%;
  background: white;
  border-radius: var(--border-radius);

  & .MuiInput-root {
    height: var(--input-height);
    padding-left: 1rem;
    padding-right: 0 !important;
    border: ${({ theme }) => `var(--border-size) solid ${theme.palette.dream.grayMedium}`};
    border-radius: var(--border-radius);
  }

  & .MuiButton-root {
    border-radius: 0 var(--border-radius) var(--border-radius) 0 !important;
    margin: calc((var(--border-size) * -1));
    margin-right: calc((var(--border-size) * -1) - 1px);
    height: var(--input-height);
    box-shadow: none;
  }
`;

const IndustrySearchContainer = styled("div")`
  padding: 1rem;
  border-top: 1px solid ${({ theme }) => theme.palette.dream.grayMedium};
`;

const IndustrySearchTitle = styled("h2")`
  font-weight: 400;
  font-size: 1rem;
  margin: 0 0 0.75rem 0;
  padding: 0;
`;

const IndustrySearchList = styled("div")`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const IndustryLink = styled(Link)`
  font-size: 1rem;
  text-decoration: none;
  background-color: ${({ theme }) => theme.palette.dream.secondaryBackground};
  color: ${({ theme }) => theme.palette.dream.grayMediumDark};
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  text-align: center;

  &:hover {
    background-color: ${({ theme }) => theme.palette.dream.grayMedium};
  }
`;

const ListItemContent = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const RecommendationContainer = styled("div")`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const AdornmentIconContainer = styled("div")`
  padding-right: 1rem;
  color: "#ababab"; /* color of placeholder */
`;

const StyledListItem = styled(ListItem)`
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;
